import { Component, NgZone } from '@angular/core';

import { BaseAlertsOutletComponent } from './base-alerts-outlet.component';
import { AlertsService } from './alerts.service';
import { IAlert, IAlertAction } from './alerts.interfaces';

@Component({
  selector: 'angular-alerts-outlet',
  templateUrl: './angular-alerts-outlet.component.html',
  styleUrls: ['./angular-alerts-outlet.component.scss'],
  standalone: false,
})
export class AngularAlertsOutletComponent extends BaseAlertsOutletComponent {
  alerts: IAlert[] = [];
  private readonly dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

  constructor(
    service: AlertsService,
    private readonly zone: NgZone,
  ) {
    super(service);
  }

  dismissAllAlerts(): void {
    this.clearDismissTimers();
    this.alerts = [];
  }

  showAlert(newAlert: IAlert): void {
    this.zone.run(() => {
      this.alerts.push(this.prepareAlert(newAlert));
      const dismissTimer = setTimeout(() => {
        this.close(newAlert.id);
      }, newAlert.duration || this.service.alertDuration);
      this.dismissTimers.set(newAlert.id, dismissTimer);
    });
  }

  // Dismiss through the service so the alert also leaves the shared queue; the service
  // echoes the id on `dismissAlert$`, which the base outlet routes back to
  // `dismissById()` below.
  close(id: string): void {
    this.service.dismissById(id);
  }

  override dismissById(id: string): void {
    clearTimeout(this.dismissTimers.get(id));
    this.dismissTimers.delete(id);
    this.alerts = this.alerts.filter(item => item.id !== id);
  }

  handleAction(action: IAlertAction, alertId: string): void {
    action.action();
    // Optionally close the alert after action is performed
    this.close(alertId);
  }

  override ngOnDestroy(): void {
    this.clearDismissTimers();
    super.ngOnDestroy();
  }

  private clearDismissTimers(): void {
    for (const dismissTimer of this.dismissTimers.values()) {
      clearTimeout(dismissTimer);
    }
    this.dismissTimers.clear();
  }

  // Copy rather than mutate: the queued alert object is shared with the service (and
  // may be replayed to a later outlet), so appending the close action in place would
  // stack one per show.
  private prepareAlert(newAlert: IAlert): IAlert {
    return {
      ...newAlert,
      actions: [
        ...(newAlert.actions ?? []),
        {
          label: '✖',
          action: (): void => {
            this.close(newAlert.id);
          },
        },
      ],
    };
  }
}
